import { useCallback, useEffect } from 'react';

interface ClickOutsideProps {
  enabled: boolean;
  elementRef: React.RefObject<HTMLElement>;
  onClickOutside: (event: MouseEvent) => void;
  skipSelectors?: string[];
}

/**
 * Hook to handle click outside of an element
 * @description
 * This hook has sibling hook useElementOuterClick
 */
export const useClickOutside = ({ enabled, elementRef, onClickOutside, skipSelectors }: ClickOutsideProps) => {
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const target = event.target as Node;
      if (skipSelectors?.length) {
        const hasMatches = skipSelectors.some((selector) => {
          const selectorElement = document.querySelector(selector);
          return selectorElement?.contains(target);
        });

        if (hasMatches) {
          return;
        }
      }
      if (target instanceof Element && target.hasAttribute('data-ignore-outside-click')) {
        // console.info('click outside ignored due to data-ignore-outside-click attribute');
        return;
      }
      if (!elementRef.current || elementRef.current.contains(target)) {
        return;
      }

      setTimeout(() => onClickOutside(event));
    },
    [elementRef, onClickOutside, skipSelectors]
  );
  useEffect(() => {
    if (enabled) {
      document.addEventListener('mouseup', handleClickOutside);
    } else {
      document.removeEventListener('mouseup', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
    };
  }, [enabled, handleClickOutside]);
};
